import React from "react";
import Header from "./common/Header";
import Footer from "./common/Footer";
import Navigation from "./common/Navigation";
import Loading from "../common/Loading";
import MessageBox, {
  IMessageBoxProps,
  IMessageBoxParams,
} from "../common/MessageBox";
import ProjectMainSpace from "./project/ProjectMainSpace";
import NoticeMainSpace from "./notice/NoticeMainSpace";
import LicenseMainSpace from "./license/LicenseMainSpace";
import ConfirmDialog, {
  IConfirmDialogProps,
  IConfirmDialogParams,
} from "../common/ConfirmDialog";
import { getNoticeCount } from "../webapi/service/getNoticeCount";
import { INoticesRow } from "../webapi/service/getNotices";
import { AppConfig, MainSpace } from "../common/constants";
import AuthService from "../common/AuthService";
import { withTranslation, WithTranslation } from "react-i18next";
import CookieConsentMessage from "./common/CookieConsent";
import "./main.css";

export interface IProps extends WithTranslation {
  /**
   * 認証サービス
   */
  authService?: AuthService;
  /**
   * ログインユーザー名
   */
  name?: string;
  /**
   * ログアウトする場合に呼び出します
   */
  onLogout?: () => void;
  /**
   * 致命的なエラーの画面を表示する場合に呼び出します
   * @param status - httpステータスコード
   */
  onFatalError: (status: number | undefined) => void;
}

interface IState {
  /**
   * 表示する画面
   * MainSpace.PROJECTS | MainSpace.NOTICE | MainSpace.LISENCE
   */
  mode: number;
  /**
   * メインスペースの幅
   */
  mainSpaceWidth: number;
  /**
   * Naviバーを開いた状態にする場合はtrueを設定する
   */
  naviOpen: boolean;
  /**
   * 通知のuri
   */
  uri?: string;
  /**
   * 同一メインスペースの場合でもプロジェクト一覧を表示する場合はtrue
   */
  forceLoad?: boolean;
  /**
   * ローディング画面を表示する場合はtrue
   */
  loading: boolean;
  /**
   * 初回ロードの場合はtrue
   */
  isFirstLoading: boolean;
  /**
   * 通知件数
   */
  noticeCount: number;
  /**
   * フッターを固定する場合はtrue
   */
  fixedFooter: boolean;
  /**
   * メッセージBoxパラメータ
   */
  messageBox: IMessageBoxProps;
  /**
   * 確認ダイアログパラメータ
   */
  confirm: IConfirmDialogProps;
  /**
   * 画面更新用name
   */
  renewname: string | undefined;
}

class MainPage extends React.Component<IProps, IState> {
  private noticeCountTimer: NodeJS.Timeout | undefined;
  private unoperatedTimeout: NodeJS.Timeout | undefined;
  private resizeObserver: ResizeObserver | undefined;
  private mainRef = React.createRef<HTMLInputElement>();

  constructor(props: IProps) {
    super(props);

    this.state = {
      mode: 0,
      mainSpaceWidth: 0,
      naviOpen: true,
      loading: true,
      isFirstLoading: true,
      noticeCount: 0,
      fixedFooter: false,
      messageBox: { show: false, title: "", message: "" },
      confirm: { show: false, title: "", message: "" },
      renewname: "",
    };

    this.handleShowMainSpace = this.handleShowMainSpace.bind(this);
    this.handleLoading = this.handleLoading.bind(this);
    this.handleShowMessage = this.handleShowMessage.bind(this);
    this.handleCloseMessage = this.handleCloseMessage.bind(this);
    this.handleOpenConfirm = this.handleOpenConfirm.bind(this);
    this.handleConfirmOk = this.handleConfirmOk.bind(this);
    this.handleConfirmCancel = this.handleConfirmCancel.bind(this);
    this.handleCommunicationError = this.handleCommunicationError.bind(this);
    this.handleSelectNotice = this.handleSelectNotice.bind(this);
    this.handlefromNoticeLoaded = this.handlefromNoticeLoaded.bind(this);
    this.handleForceLoaded = this.handleForceLoaded.bind(this);
    this.handleNaviOpen = this.handleNaviOpen.bind(this);
    this.handleResetUnoperatedTimeout =
      this.handleResetUnoperatedTimeout.bind(this);
  }

  componentDidMount() {
    this.startNoticeCountTimer();
    this.startUnoperatedTimeout();
    this.setUserName();

    this.resizeObserver = new ResizeObserver((entries) => {
      const { target } = entries[0];
      const { scrollHeight, clientHeight, clientWidth } = target;
      //console.log({ scrollHeight: scrollHeight, clientHeight: clientHeight });
      const fixedFooter = scrollHeight > clientHeight ? false : true;
      if (fixedFooter !== this.state.fixedFooter) {
        this.setState({
          fixedFooter: fixedFooter,
          mainSpaceWidth: clientWidth,
        });
      } else {
        this.setState({ mainSpaceWidth: clientWidth });
      }
    });
    if (this.mainRef.current) {
      this.resizeObserver.observe(this.mainRef.current);
    }
  }

  componentWillUnmount() {
    this.stopNoticeCountTimer();
    this.stopUnoperatedTimeout();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  startNoticeCountTimer() {
    this.noticeCountTimer = setInterval(() => {
      getNoticeCount({
        authService: this.props.authService,
        success: (unread) => {
          if (this.state.noticeCount !== unread) {
            this.setState({ noticeCount: unread });
          }
        },
        fail: (id, status) => {
          this.handleCommunicationError(id, status);
        },
      });
    }, AppConfig.NOTICES_INTERVAL);
  }

  stopNoticeCountTimer() {
    if (this.noticeCountTimer) {
      clearInterval(this.noticeCountTimer);
    }
  }

  startUnoperatedTimeout() {
    if (AppConfig.MODE === AppConfig.MODE_CLOUD) {
      const { authService, t } = this.props;
      this.unoperatedTimeout = setTimeout(() => {
        this.stopNoticeCountTimer();
        if (authService) {
          authService.stopSilentRenew();
        }
        this.handleShowMessage({
          title: t("unoperated_timeout_title"),
          message: t("unoperated_timeout_message"),
          onClose: () => {
            this.handleLoading(true);
            authService?.logout();
          },
        });
      }, AppConfig.UNOPERATED_TIMEOUT);
    }
  }

  stopUnoperatedTimeout() {
    if (AppConfig.MODE === AppConfig.MODE_CLOUD) {
      if (this.unoperatedTimeout) {
        clearInterval(this.unoperatedTimeout);
      }
    }
  }

  handleResetUnoperatedTimeout() {
    if (AppConfig.MODE === AppConfig.MODE_CLOUD) {
      //console.log("resetUnoperatedTimeout");
      this.stopUnoperatedTimeout();
      this.startUnoperatedTimeout();
    }
  }

  handleShowMainSpace(mode: number) {
    this.handleResetUnoperatedTimeout();
    if (mode !== this.state.mode) {
      this.setState({ mode: mode, forceLoad: undefined });
    } else {
      this.setState({ forceLoad: true });
    }
  }

  handleLoading(loading: boolean) {
    if (!loading && this.state.isFirstLoading) {
      this.setState({ isFirstLoading: false });
    }
    if (this.state.loading !== loading) {
      this.setState({ loading: loading });
    }
  }

  handleShowMessage(params: IMessageBoxParams) {
    this.setState({
      messageBox: {
        show: true,
        title: params.title,
        message: params.message,
        onClose: params.onClose,
      },
    });
  }

  handleCloseMessage() {
    const { onClose } = this.state.messageBox;
    const close: (() => void) | undefined =
      typeof onClose !== "undefined" ? onClose : undefined;
    this.setState({
      messageBox: {
        show: false,
        title: "",
        message: "",
        onClose: undefined,
      },
    });
    if (typeof close !== "undefined") {
      close();
    }
  }

  handleOpenConfirm(params: IConfirmDialogParams) {
    this.setState({
      confirm: {
        show: true,
        title: params.title,
        message: params.message,
        onOk: params.onOk,
        onCancel: params.onCancel,
      },
    });
  }

  handleConfirmOk() {
    const { onOk } = this.state.confirm;
    const ok: (() => void) | undefined =
      typeof onOk !== "undefined" ? onOk : undefined;
    this.setState({
      confirm: {
        show: false,
        title: "",
        message: "",
        onOk: undefined,
        onCancel: undefined,
      },
    });
    if (typeof ok !== "undefined") {
      ok();
    }
  }

  handleConfirmCancel() {
    const { onCancel } = this.state.confirm;
    const cancel: (() => void) | undefined =
      typeof onCancel !== "undefined" ? onCancel : undefined;
    this.setState({
      confirm: {
        show: false,
        title: "",
        message: "",
        onOk: undefined,
        onCancel: undefined,
      },
    });
    if (typeof cancel !== "undefined") {
      cancel();
    }
  }

  handleCommunicationError(
    id: string | undefined,
    status: number | undefined,
    onCloseMessage?: () => void
  ) {
    if (typeof id === "undefined") {
      this.props.onFatalError(status);
      this.handleLoading(false);
    } else {
      this.handleLoading(false);
      this.handleShowMessage({
        title: "Error",
        message: this.props.t(id),
        onClose: onCloseMessage,
      });
    }
  }

  handleSelectNotice(data: INoticesRow | undefined) {
    if (data) {
      this.setState({ mode: MainSpace.PROJECTS, uri: data.uri });
    } else {
      this.handleShowMainSpace(MainSpace.NOTICE);
    }
  }

  handlefromNoticeLoaded() {
    this.setState({ uri: undefined });
  }

  handleForceLoaded() {
    this.setState({ forceLoad: undefined });
  }

  handleNaviOpen(open: boolean) {
    this.setState({ naviOpen: open });
  }

  setUserName() {
    const { authService, onFatalError } = this.props;
    if (authService) {
      authService.getUserName({
        success: (name) => {
         this.setState({ renewname: name });
        },
        fail: (status) => {
          onFatalError(status);
        }
      });
    }
  }

  getMainSpace() {
    let mainspace = <div></div>;
    switch (this.state.mode) {
      case MainSpace.PROJECTS:
        mainspace = (
          <ProjectMainSpace
            authService={this.props.authService}
            mainSpaceWidth={this.state.mainSpaceWidth}
            uri={this.state.uri}
            forceLoad={this.state.forceLoad}
            onFromNoticeLoaded={this.handlefromNoticeLoaded}
            onForceLoaded={this.handleForceLoaded}
            isLoading={this.state.loading}
            onLoading={this.handleLoading}
            showMessage={this.handleShowMessage}
            showConfirm={this.handleOpenConfirm}
            onCommunicationError={this.handleCommunicationError}
            resetUnoperatedTimeout={this.handleResetUnoperatedTimeout}
          />
        );
        break;
      case MainSpace.NOTICE:
        mainspace = (
          <NoticeMainSpace
            authService={this.props.authService}
            onNotice={this.handleSelectNotice}
            onLoading={this.handleLoading}
            onCommunicationError={this.handleCommunicationError}
            resetUnoperatedTimeout={this.handleResetUnoperatedTimeout}
          />
        );
        break;
      case MainSpace.LISENCE:
        mainspace = (
          <LicenseMainSpace
            authService={this.props.authService}
            onLoading={this.handleLoading}
            showConfirm={this.handleOpenConfirm}
            onCommunicationError={this.handleCommunicationError}
            resetUnoperatedTimeout={this.handleResetUnoperatedTimeout}
          />
        );
        break;
    }
    return mainspace;
  }

  render() {
    const { authService, name, onLogout } = this.props;
    const {
      mode,
      naviOpen,
      noticeCount,
      loading,
      isFirstLoading,
      fixedFooter,
      messageBox,
      confirm,
      renewname,
    } = this.state;
    const className = naviOpen ? "main-mainspace" : "main-mainspace-navi-close";
    const footerClassName =
      loading || fixedFooter
        ? naviOpen
          ? "footer-fixed"
          : "footer-fixed-navi-close"
        : "footer";
    const mainspace = this.getMainSpace();  
    const username = authService && name === "" ? renewname : name
    return (
      <React.Fragment>
        <Header
          authService={authService}
          naviOpen={naviOpen}
          onNaviOpen={this.handleNaviOpen}
          onTop={() => this.handleShowMainSpace(MainSpace.PROJECTS)}
          noticeCount={noticeCount}
          onNotice={this.handleSelectNotice}
          name={username}
          onLogout={onLogout}          
          onLicense={
            authService
              ? () => this.handleShowMainSpace(MainSpace.LISENCE)
              : undefined
          }
          onCommunicationError={this.handleCommunicationError}
        />
        <main>
          <Navigation
            mode={mode}
            onSelect={this.handleShowMainSpace}
            open={naviOpen}
          />
          <div className={className} ref={this.mainRef}>
            {mainspace}
            <Footer className={footerClassName} />
          </div>
          <CookieConsentMessage />
        </main>
        <div style={{ display: loading ? "" : "none" }}>
          <Loading transparent={!isFirstLoading} />
        </div>
        <MessageBox
          show={messageBox.show}
          title={messageBox.title}
          message={messageBox.message}
          onClose={this.handleCloseMessage}
        />
        <ConfirmDialog
          show={confirm.show}
          title={confirm.title}
          message={confirm.message}
          onOk={this.handleConfirmOk}
          onCancel={this.handleConfirmCancel}
        />
      </React.Fragment>
    );
  }
}

export default withTranslation()(MainPage);
